/**
 * Notifications Center — Stage 9.
 *
 * Aggregates the `notification.Notification` log into "campaign-ish"
 * buckets keyed by topic (or title fallback), and computes a heuristic
 * attribution to orders placed within a follow-up window. Backed by
 * `/api/bi/notifications/campaigns/` and `/api/bi/notifications/attribution/`.
 *
 * Until the comms service grows real campaign IDs and delivery webhooks,
 * this is the fastest honest read of "what we're sending and who's
 * placing an order soon after." Microcopy is upfront about it.
 */
import { useMemo, useState } from 'react';
import { Megaphone, Send, Users, Image as ImageIcon, Target } from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import { Badge } from '@/components/ui/Badge';
import { Card, CardBody, CardHeader, CardTitle } from '@/components/ui/Card';
import { ErrorState } from '@/components/ui/ErrorState';
import { EmptyState } from '@/components/ui/EmptyState';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/Tabs';
import { KPICard } from '@/components/charts/KPICard';
import { Sparkline } from '@/components/charts/Sparkline';
import { DataView } from '@/components/data/DataView';
import { useNotificationsCampaigns, useNotificationsAttribution } from '@/api/bi';
import type { NotifTopicRow, NotifAttributionRow } from '@/api/bi';
import { rangeISO, useFilters } from '@/state/filters';
import { fmt } from '@/lib/format';
import { cn } from '@/lib/cn';

type TabId = 'campaigns' | 'attribution';

export function NotificationsPage() {
  const range = useFilters((s) => s.range);
  const timeRangeMode = useFilters((s) => s.timeRangeMode);
  const { start: startISO, end: endISO } = useMemo(
    () => rangeISO(range, timeRangeMode),
    [range, timeRangeMode],
  );

  const [tab, setTab] = useState<TabId>('campaigns');
  const [windowHours, setWindowHours] = useState<number>(24);

  const campaigns = useNotificationsCampaigns({ start: startISO, end: endISO });
  const attribution = useNotificationsAttribution({
    start: startISO,
    end: endISO,
    windowHours,
  });

  const camp = campaigns.data;
  const totals = camp?.totals;

  const dailySent = useMemo(
    () => (camp?.daily ?? []).map((d) => ({ value: d.sent })),
    [camp?.daily],
  );
  const dailyUsers = useMemo(
    () => (camp?.daily ?? []).map((d) => ({ value: d.unique_users })),
    [camp?.daily],
  );

  const attr = attribution.data;
  const attrTotals = attr?.totals;
  const attributionRows = useMemo<NotifAttributionRow[]>(
    () => (attr?.topics ?? []).slice(),
    [attr?.topics],
  );

  return (
    <>
      <PageHeader
        eyebrow="Comms"
        title="Notifications"
        description="What we're broadcasting, who it's reaching, and which pings actually nudge an order."
      />

      <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <KPICard
          label="Notifications sent"
          value={fmt.number(totals?.sent ?? 0)}
          loading={campaigns.isLoading}
          trend={dailySent}
        />
        <KPICard
          label="People reached"
          value={fmt.number(totals?.unique_users ?? 0)}
          loading={campaigns.isLoading}
          trend={dailyUsers}
          hint="Distinct logged-in users with a registered FCM device that received at least one ping."
        />
        <KPICard
          label="Distinct topics"
          value={fmt.number(totals?.unique_topics ?? 0)}
          loading={campaigns.isLoading}
        />
        <KPICard
          label="Avg fan-out"
          value={`${(totals?.fanout ?? 0).toFixed(1)}×`}
          loading={campaigns.isLoading}
          hint="Pings per recipient. > 1.0 means we sent the same person more than one in this window."
        />
      </div>

      <div className="mt-5">
        <Tabs value={tab} onValueChange={(v) => setTab(v as TabId)}>
          <TabsList className="mb-3">
            <TabsTrigger value="campaigns">
              <Megaphone className="mr-1.5 h-3.5 w-3.5" /> Campaigns
            </TabsTrigger>
            <TabsTrigger value="attribution">
              <Target className="mr-1.5 h-3.5 w-3.5" /> Attribution
            </TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      {tab === 'campaigns' && (
        <CampaignsView
          loading={campaigns.isLoading}
          error={campaigns.isError}
          retry={() => void campaigns.refetch()}
          topics={camp?.topics ?? []}
        />
      )}

      {tab === 'attribution' && (
        <AttributionView
          loading={attribution.isLoading}
          error={attribution.isError}
          retry={() => void attribution.refetch()}
          windowHours={windowHours}
          onWindowChange={setWindowHours}
          rows={attributionRows}
          totalRecipients={attrTotals?.recipients ?? 0}
          totalAttributed={attrTotals?.attributed_orders ?? 0}
          attributionRate={attrTotals?.attribution_rate ?? 0}
        />
      )}
    </>
  );
}

function CampaignsView({
  loading,
  error,
  retry,
  topics,
}: {
  loading: boolean;
  error: boolean;
  retry: () => void;
  topics: NotifTopicRow[];
}) {
  const [search, setSearch] = useState('');
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return topics;
    return topics.filter((t) => t.topic.toLowerCase().includes(q));
  }, [topics, search]);
  const peakSent = Math.max(1, ...topics.map((t) => t.sent));

  if (error) return <ErrorState description="Couldn't load campaigns." onRetry={retry} />;

  return (
    <DataView<NotifTopicRow>
      title="Topics & titles"
      description="Each row groups all notifications by topic (or fallback title) for the selected window."
      data={filtered}
      isLoading={loading}
      search={{ value: search, onChange: setSearch, placeholder: 'Search topic…' }}
      emptyTitle="Quiet on the wire"
      emptyDescription="No notifications in this window. Either nothing was sent or the topic field wasn't tagged."
      columns={[
        {
          accessorKey: 'topic',
          header: 'Topic',
          cell: ({ row }) => (
            <div className="flex items-center gap-2">
              <Megaphone className="h-3.5 w-3.5 text-muted-foreground" />
              <span className="truncate font-medium text-foreground">{row.original.topic}</span>
            </div>
          ),
        },
        {
          accessorKey: 'sent',
          header: 'Sent',
          cell: ({ row }) => (
            <div className="flex items-center gap-2">
              <span className="w-12 text-right font-mono text-xs">{fmt.number(row.original.sent)}</span>
              <div className="h-1.5 w-24 overflow-hidden rounded-full bg-muted">
                <div
                  className="h-full bg-primary/70"
                  style={{ width: `${(row.original.sent / peakSent) * 100}%` }}
                />
              </div>
            </div>
          ),
        },
        {
          accessorKey: 'unique_users',
          header: 'Reached',
          cell: ({ row }) => (
            <span className="inline-flex items-center gap-1 text-xs">
              <Users className="h-3 w-3 text-muted-foreground" />
              {fmt.number(row.original.unique_users)}
            </span>
          ),
        },
        {
          accessorKey: 'fanout',
          header: 'Fan-out',
          cell: ({ row }) => (
            <Badge tone={row.original.fanout > 2 ? 'warning' : 'neutral'}>
              {row.original.fanout.toFixed(1)}×
            </Badge>
          ),
        },
        {
          accessorKey: 'with_image',
          header: 'Image',
          cell: ({ row }) =>
            row.original.with_image > 0 ? (
              <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                <ImageIcon className="h-3 w-3" /> {fmt.number(row.original.with_image)}
              </span>
            ) : (
              <span className="text-xs text-muted-foreground">—</span>
            ),
        },
      ]}
    />
  );
}

function AttributionView({
  loading,
  error,
  retry,
  windowHours,
  onWindowChange,
  rows,
  totalRecipients,
  totalAttributed,
  attributionRate,
}: {
  loading: boolean;
  error: boolean;
  retry: () => void;
  windowHours: number;
  onWindowChange: (n: number) => void;
  rows: NotifAttributionRow[];
  totalRecipients: number;
  totalAttributed: number;
  attributionRate: number;
}) {
  if (error) return <ErrorState description="Couldn't load attribution." onRetry={retry} />;
  const peakRate = Math.max(0.0001, ...rows.map((r) => r.attribution_rate));

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle>Attribution window</CardTitle>
          <div className="flex items-center gap-1">
            {[6, 12, 24, 48, 72].map((h) => (
              <button
                key={h}
                onClick={() => onWindowChange(h)}
                className={cn(
                  'press rounded-full px-2.5 py-0.5 text-[11px] font-medium transition-colors',
                  windowHours === h
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-muted text-muted-foreground hover:bg-muted/70',
                )}
              >
                {h}h
              </button>
            ))}
          </div>
        </CardHeader>
        <CardBody>
          <div className="grid gap-3 sm:grid-cols-3">
            <Stat label="Recipients" value={fmt.number(totalRecipients)} icon={<Users className="h-3.5 w-3.5" />} />
            <Stat label="Attributed orders" value={fmt.number(totalAttributed)} icon={<Send className="h-3.5 w-3.5" />} />
            <Stat
              label="Attribution rate"
              value={fmt.pct(attributionRate)}
              icon={<Target className="h-3.5 w-3.5" />}
              hint="Heuristic — counts at most one order per recipient per topic per window."
            />
          </div>
        </CardBody>
      </Card>

      <Card className="mt-4">
        <CardHeader>
          <CardTitle>Topics ranked by attributed orders</CardTitle>
          <Badge tone="neutral">{rows.length}</Badge>
        </CardHeader>
        <CardBody>
          {loading ? (
            <div className="space-y-2">
              {Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="h-9 animate-pulse rounded-md bg-muted/40" />
              ))}
            </div>
          ) : rows.length === 0 ? (
            <EmptyState
              title="Nothing attributable"
              description="Either nothing was sent or no recipient placed an order inside the window."
            />
          ) : (
            <ul className="space-y-1.5">
              {rows.map((r) => (
                <li
                  key={r.topic}
                  className="flex items-center gap-3 rounded-md border border-border bg-surface px-3 py-2"
                >
                  <Megaphone className="h-3.5 w-3.5 text-muted-foreground" />
                  <span className="min-w-0 flex-1 truncate text-sm font-medium">{r.topic}</span>
                  <span className="font-mono text-xs tabular-nums text-muted-foreground">
                    {fmt.number(r.attributed_orders)} / {fmt.number(r.recipients)}
                  </span>
                  <div className="h-1.5 w-28 overflow-hidden rounded-full bg-muted">
                    <div
                      className="h-full bg-success/80"
                      style={{ width: `${(r.attribution_rate / peakRate) * 100}%` }}
                    />
                  </div>
                  <Badge tone={r.attribution_rate > 0.05 ? 'success' : 'neutral'}>
                    {fmt.pct(r.attribution_rate)}
                  </Badge>
                </li>
              ))}
            </ul>
          )}
        </CardBody>
      </Card>
    </>
  );
}

function Stat({
  label,
  value,
  icon,
  hint,
}: {
  label: string;
  value: string;
  icon: React.ReactNode;
  hint?: string;
}) {
  return (
    <div className="rounded-lg border border-border bg-surface p-3">
      <div className="flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
        {icon} {label}
      </div>
      <div className="mt-1 text-xl font-semibold tabular-nums text-foreground">{value}</div>
      {hint ? <p className="mt-0.5 text-[11px] text-muted-foreground">{hint}</p> : null}
    </div>
  );
}

// Sparkline retained for future per-topic trend lines.
export const _spark = Sparkline;
